/**
 * @module app.cm
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { bindable } from 'aurelia-typed-observable-plugin';
import { CaseService } from 'app/cm/services/case-service';
import { NoteService } from 'app/core/services/note-service';
import { allActionsSucceeded, apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { localSort } from 'app/shared/lib/sorting';
import { Toaster } from 'app/shared/lib/toaster';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class AddNote extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: INotesConfig = {};

    protected cases: CM.Case[] = [];
    protected selectedCases: CM.Case[] = [];
    protected localSorting = new Sorting();

    @observable
    protected category: Core.NoteCategoryWithSubCategoryModel | null = null;
    protected subCategory: Core.NoteSubCategory | null = null;
    protected flags: Core.NoteFlag[] = [];

    @observable
    protected isCustom: boolean = false;

    @observable
    protected definition: Core.NoteDefinition | null = null;

    protected text: string = '';
    protected customText: string = '';

    protected categories: Core.NoteCategory[] = [];

    protected maxTextLength: number = 4000;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly context: HouseholdCasePageContext,
        private readonly noteService: NoteService,
        private readonly caseService: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        const maxLengthSetting = getApiSetting(this.context.settings, 'text.$maximumLength');
        if(maxLengthSetting) { this.maxTextLength = maxLengthSetting as number; }

        this.noteService.configure(this.config);
        this.reload();
    }

    ////****************** Events ***************************************************////

    protected categoryChanged(): void {
        if(this.category && this.category.flags) {
            this.flags = [...this.category.flags];
        } else {
            this.flags = [];
        }

        if(this.category && this.category.subCategories && this.category.subCategories.length === 1) {
            this.subCategory = this.category.subCategories[0];
        } else {
            this.subCategory = null;
        }
    }

    protected isCustomChanged(): void {
        if(!this.isCustom && this.text) {
            // when switching to standard notes, stash custom text
            this.customText = this.text;
        }
    }

    protected definitionChanged(): void {
        if(this.definition && this.definition.id && this.definition.text) {
            // use definition's text in case the user switches back to custom (using it as a template)
            this.text = this.definition.text
        }
        else if(this.customText) {
            // when no definition is selected, switch text back to what it was before for convenience
            this.text = this.customText;
        }
    }

    ////****************** Functions ************************************************////

    private async reload(): Promise<void> {
        await this.loadLookups();
    }

    protected buildNote(memberCase: CM.Case): CM.CaseNote {
        const saveNote: CM.CaseNote = {
            category: getReference(this.category, { subCategory: getReference(this.subCategory) || undefined }),
            flags: getReferences(this.flags),
            definition: getReference(!this.isCustom && this.definition || undefined) || undefined,
            text: this.isCustom && this.text || undefined,
            case: getReference(memberCase)
        };

        return saveNote;
    }

    protected hasDefinitions(category: Core.NoteCategory): boolean {
        const hasDefinitions = (category?.definitions?.length || 0) > 0;

        this.isCustom = !hasDefinitions;
        return hasDefinitions;
    }

    ////****************** IForm ****************************************************////

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.addNote();
    }

    public reset(): void {
        this.selectedCases.splice(0);
        this.category = null;
        this.subCategory = null;
        this.flags.splice(0);
        this.isCustom = false;
        this.definition = null;
        this.text = '';
        this.customText = '';
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    public async loadLookups(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = { all: true };
        const caseIncludes: string[] = ['member'];
        const categoryIncludes: string[] = ['flags', 'definitions', 'subcategories'];

        try {
            this.state.beginLoad();

            const [cases, categories] = await Promise.all(
                [
                    this.caseService.loadCasesByHouseholdCase(this.householdCaseId, options, caseIncludes),
                    this.noteService.loadCategories({ all: true }, categoryIncludes, this.config.excludeSystem)
                ]
            )

            this.categories = categories.results;
            if(this.category && this.category.subCategories && this.category.subCategories.length === 1) {
                this.subCategory = this.category.subCategories[0];
            }

            this.cases = cases.results;
            // use local sorting to sort cases by member description
            this.localSorting.entries = [
                {
                    reference: 'member.description',
                    direction: SortDirection.Ascending
                }
            ];
            localSort(this.cases, this.localSorting);
        } catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async addNote(): Promise<boolean> {
        const insertNotes: CM.CaseNote[] = [];

        this.selectedCases.forEach(c => {
            insertNotes.push(this.buildNote(c));
        })

        let success = false;
        try {
            this.state.beginSave();
            const progressToast = this.toaster.standard.batch('core:note.$', insertNotes.length);
            progressToast.show();

            const responses = await this.noteService.insertNoteBatch(insertNotes, progressToast);
            success = allActionsSucceeded(responses);

            return success;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
